import { AdjustmentsIcon } from "@heroicons/react/outline";
import React from "react";
import { BracesIcon } from "lucide-react";

import WorkflowSelectionSection from "./WorkflowSelectionSection";
import ReturnAllOutputsToggle from "./ReturnAllOutputsToggle";
import VerboseModeToggle from "./VerboseModeToggle";

import InputVariablesField from "@/components/PowerBlock/Block/InputVariablesField";
import SectionGroup from "@/components/PowerBlock/Block/SectionGroup";
import { SourceType } from "@/components/PowerBlock/Block/SourcesField/SelectSource";
import { Entity, EntityType } from "@/queries";

interface ConfigurationViewProps {
  workflowName?: string;
  workflowId?: number;
  workflowVersionNumber?: number;
  workflowLabelName?: string;
  folderId?: number | null;
  isLoading: boolean;
  inputVariables: string[];
  sources: Record<SourceType, string[]>;
  showVerboseMode: boolean;
  verboseEnabled: boolean;
  returnAllOutputsEnabled: boolean;
  inputVariablesIsLoading: boolean;
  onWorkflowSelection: (entity: Entity<EntityType.WORKFLOW>) => void;
  onVerboseChange: (enabled: boolean) => void;
  onReturnAllOutputsChange: (enabled: boolean) => void;
  onAutofill: (mappings: Record<string, string>) => void;
  hasError?: unknown;
}

const ConfigurationView = React.memo(
  ({
    workflowName,
    workflowId,
    workflowVersionNumber,
    workflowLabelName,
    folderId,
    isLoading,
    inputVariables,
    sources,
    showVerboseMode,
    verboseEnabled,
    returnAllOutputsEnabled,
    inputVariablesIsLoading,
    onWorkflowSelection,
    onVerboseChange,
    onReturnAllOutputsChange,
    onAutofill,
    hasError,
  }: ConfigurationViewProps) => (
    <div className="space-y-4">
      <WorkflowSelectionSection
        workflowName={workflowName}
        workflowId={workflowId}
        workflowVersionNumber={workflowVersionNumber}
        workflowLabelName={workflowLabelName}
        folderId={folderId}
        isLoading={isLoading}
        onWorkflowSelection={onWorkflowSelection}
        hasError={hasError}
      />

      {/* Input Variables Section */}
      {(inputVariables.length > 0 || (workflowName && (isLoading || inputVariablesIsLoading))) && (
        <SectionGroup
          title="Input Variables"
          description="Map workflow variables to data sources"
          variant="default"
          icon={<BracesIcon className="h-4 w-4" />}
        >
          <InputVariablesField
            variables={inputVariables}
            sources={sources}
            sourceKeyPrefix="input_variables"
            isLoading={inputVariablesIsLoading}
            showAutofill
            onAutofill={onAutofill}
          />
        </SectionGroup>
      )}

      {/* Advanced Settings Section */}
      {showVerboseMode && (
        <SectionGroup
          title="Advanced Settings"
          variant="compact"
          collapsible
          defaultExpanded={false}
          icon={<AdjustmentsIcon className="h-4 w-4" />}
        >
          <div className="space-y-4">
            <VerboseModeToggle
              enabled={verboseEnabled}
              onChange={onVerboseChange}
              showVerboseMode={showVerboseMode}
              isLoading={isLoading}
            />
            <ReturnAllOutputsToggle
              enabled={returnAllOutputsEnabled}
              onChange={onReturnAllOutputsChange}
              isLoading={isLoading}
            />
          </div>
        </SectionGroup>
      )}
    </div>
  ),
  (prevProps, nextProps) => {
    const keysToCompare = [
      "workflowName",
      "workflowId",
      "workflowVersionNumber",
      "workflowLabelName",
      "folderId",
      "isLoading",
      "showVerboseMode",
      "verboseEnabled",
      "returnAllOutputsEnabled",
      "inputVariablesIsLoading",
      "hasError",
    ] as const;

    for (const key of keysToCompare) {
      if (prevProps[key] !== nextProps[key]) {
        return false;
      }
    }

    // Deep comparison for complex objects
    if (JSON.stringify(prevProps.inputVariables) !== JSON.stringify(nextProps.inputVariables)) {
      return false;
    }
    if (JSON.stringify(prevProps.sources) !== JSON.stringify(nextProps.sources)) {
      return false;
    }

    return true;
  },
);

ConfigurationView.displayName = "ConfigurationView";

export default ConfigurationView;
